import { PlanungAdminRoles, PlanungViewerRoles } from '@bp/pim-auth-constants';
import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  Row,
  Table,
  TableColumns,
  Tooltip,
  useDefaultSorting,
} from '@bp/ui-components';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  UserStatus,
  useAddUserMutation,
  useInviteUserMutation,
  usePimProfilesQuery,
  useRemovePlanungAccessMutation,
  useUpdateUserPermissionsMutation,
} from '../../../types/planung-graphql-client-defs';
import { getRoleNameTranslation } from '../../../utils/pim-user/getRoleNameTranslation';
import { getUserStatusTranslation } from '../../../utils/pim-user/getUserStatusTranslation';
import { showInviteEmailSendSuccessToast, showSuccessSaveToast } from '../../../utils/toast';
import { AddUserModal } from '../modals/AddUserModal';
import { InviteModal } from '../modals/InviteModal';
import { UserPermissionsModal } from '../modals/UserPermissionsModal';
import { Permissions } from './components/Permissions';
import styles from './PimUserTable.module.scss';

export type PimUserDataType = {
  uuid: string;
  lastName: string;
  firstName: string;
  userEmail: string;
  profileEmail: string;
  accessGranted: boolean;
  selectName: string;
  userStatusTranslation: string;
  userStatus: UserStatus;
  role: string;
  permissions: ReactNode;
};

export const PimUserTable = () => {
  // states
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedUserUuid, setSelectedUserUuid] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // hooks
  const { pimAuthClaims } = useAuthClaims();
  const { sorting, onSortingChange } = useDefaultSorting();
  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('pim-user-table', {});
  const { t } = useTranslation();
  const { confirm, ConfirmationDialog } = useConfirm({
    defaultTitle: t('users.removeAccessConfirmationTitle'),
  });

  const context = useMemorizedCacheTag('PIM_PROFILE');

  const [, update] = useUpdateUserPermissionsMutation();
  const [, inviteUser] = useInviteUserMutation();
  const [, removeAccess] = useRemovePlanungAccessMutation();
  const [, addUser] = useAddUserMutation();

  const [{ data: pimProfilesData, fetching }] = usePimProfilesQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
    },
    context,
  });

  const handleRemoveAccess = useCallback(
    async (uuid: string) => {
      setLoading(true);
      await removeAccess(
        {
          profileUuid: uuid,
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
        },
        context,
      );
      setLoading(false);
    },
    [pimAuthClaims, removeAccess, context],
  );

  const handleInvite = async (uuid: string, email: string) => {
    setLoading(true);
    await inviteUser(
      {
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
        profileUuid: uuid,
        frontendUri: import.meta.env.VITE_APP_BASE_URI,
        email: email,
      },
      context,
    );
    setLoading(false);
  };

  const handlePermissions = async (permissions: string[], uuid?: string | null) => {
    setLoading(true);
    if (uuid) {
      await update(
        {
          permissions: permissions,
          profileUuid: uuid,
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
        },
        context,
      );
    }
    setLoading(false);
  };

  const tableData: PimUserDataType[] = useMemo(() => {
    return (
      pimProfilesData?.pimProfiles
        .filter((pp) => pp.accessGranted)
        .map((pp) => {
          return {
            uuid: pp.uuid ?? '',
            firstName: pp.firstName ?? '',
            lastName: pp.lastName ?? '',
            userEmail: pp.userEmail ?? '',
            profileEmail: pp.profileEmail ?? '',
            accessGranted: pp.accessGranted ?? false,
            selectName: pp.selectName ?? '',
            role: getRoleNameTranslation(pp.role),
            userStatusTranslation: getUserStatusTranslation(pp.userStatus),
            userStatus: pp.userStatus ?? UserStatus.None,
            permissions: (
              <Permissions
                key={pp.uuid}
                permissions={pp.permissions as Array<PlanungAdminRoles | PlanungViewerRoles>}
              />
            ),
          };
        }) ?? []
    );
  }, [pimProfilesData]);

  const currentEditableUser = tableData.find((u) => u.uuid === selectedUserUuid);

  const memoizedColumns = useMemo((): TableColumns<PimUserDataType>[] => {
    return [
      {
        header: t('common.lastName'),
        id: 'lastName',
        accessorKey: 'lastName',
        size: 200,
      },
      {
        header: t('common.firstName'),
        id: 'firstName',
        accessorKey: 'firstName',
        size: 200,
      },

      {
        header: t('common.loginName'),
        id: 'userEmail',
        accessorKey: 'userEmail',
        canExpand: true,
        cell: ({ row }) => {
          if (row.original.userStatus !== UserStatus.Full) {
            return (
              <Tooltip content={t('users.userNotVerifiedTooltip')}>
                <div className={styles['user-not-verified']}>{row.original.userEmail}</div>
              </Tooltip>
            );
          }
          return row.original.userEmail;
        },
      },
      {
        header: t('common.status'),
        id: 'userStatusTranslation',
        accessorKey: 'userStatusTranslation',
      },
      {
        header: t('users.role', { count: 1 }),
        id: 'role',
        accessorKey: 'role',
      },
      {
        header: t('users.permission', { count: 2 }),
        id: 'permissions',
        accessorKey: 'permissions',
        enableColumnFilter: false,
        enableGlobalFilter: false,
        cell: ({ row }) => {
          return row.original.permissions;
        },
      },
    ];
  }, [t]);

  const lastCol = useCallback(
    (row: Row<PimUserDataType>) => {
      return (
        <Dropdown trigger={<Button hierarchy={'tertiary'} icon={<DotsVerticalIcon />} />}>
          <DropdownMenu
            data={[
              {
                label: t('users.permission', { count: 2 }),
                onClick: () => {
                  setSelectedUserUuid(row.original.uuid);
                  setPermissionsModalOpen(true);
                },
              },
              {
                label: t('users.reinvite'),
                disabled: row.original.userStatus === UserStatus.Full,
                onClick: async () => {
                  setSelectedUserUuid(row.original.uuid);
                  setInviteModalOpen(true);
                },
              },
              {
                type: 'ruler',
              },
              {
                disabled: !row.original.accessGranted,
                color: 'error',
                label: t('users.removeAccess'),
                onClick: async () => {
                  const confirmed = await confirm({
                    message: t('users.removeAccessConfirmationMessage', {
                      name: `${row.original.lastName}, ${row.original.firstName}`,
                    }),
                  });
                  if (confirmed) {
                    await handleRemoveAccess(row.original.uuid);
                  }
                },
              },
            ]}
          />
        </Dropdown>
      );
    },
    [t, handleRemoveAccess, confirm],
  );

  return (
    <>
      <Table<PimUserDataType>
        loading={fetching || loading}
        columns={memoizedColumns}
        data={tableData}
        sorting={sorting}
        onSortingChange={onSortingChange}
        showVisibility={true}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={saveColumnVisibility}
        lastCol={lastCol}
        showActionBar={true}
        showShadow={true}
        onAddClick={() => {
          setAddUserModalOpen(true);
        }}
        actionBarSettings={{
          showAddButton: true,
          addButtonText: t('users.add'),
        }}
        isOnWhite={false}
        showBorderRadius={true}
      />

      {currentEditableUser && (
        <InviteModal
          isOpen={inviteModalOpen}
          onClose={() => {
            setSelectedUserUuid(null);
            setInviteModalOpen(false);
          }}
          onInvite={async (email) => {
            await handleInvite(selectedUserUuid ?? '', email);
            setSelectedUserUuid(null);
            showInviteEmailSendSuccessToast(email);
          }}
          pimProfile={currentEditableUser}
        ></InviteModal>
      )}

      {addUserModalOpen && (
        <AddUserModal
          onClose={() => {
            setAddUserModalOpen(false);
            setSelectedUserUuid(null);
          }}
          isOpen={addUserModalOpen}
          onUserAdd={async (data) => {
            setLoading(true);
            await addUser(data, context);
            setLoading(false);
            setAddUserModalOpen(false);
            setSelectedUserUuid(null);
            showInviteEmailSendSuccessToast(data.email);
          }}
        />
      )}

      <UserPermissionsModal
        name={currentEditableUser ? `${currentEditableUser.firstName} ${currentEditableUser.lastName}` : ''}
        onClose={() => {
          setPermissionsModalOpen(false);
          setSelectedUserUuid(null);
        }}
        isOpen={permissionsModalOpen}
        profileUuid={selectedUserUuid ?? ''}
        onPermissionsChanged={async (permissions, profileUuid) => {
          await handlePermissions(permissions, profileUuid);
          setSelectedUserUuid(null);
        }}
      />
      <ConfirmationDialog />
    </>
  );
};
